/**
 * Browser fingerprint utilities for secure authentication
 * Captures device characteristics to be sent with login request
 */

export interface BrowserFingerprint {
  userAgent: string;
  acceptLanguage: string;
  timezone: string;
  screenResolution: string;
  colorDepth: number;
  platform: string;
  cookieEnabled: boolean;
  doNotTrack: string;
  plugins: string[];
  canvas?: string;
  webgl?: string;
}

/**
 * Capture browser fingerprint data
 * This should only be called client-side (in browser)
 */
export function captureBrowserFingerprint(): BrowserFingerprint {
  if (typeof window === 'undefined') {
    // Server-side fallback - should not happen in proper usage
    return {
      userAgent: 'node',
      acceptLanguage: 'en',
      timezone: 'UTC',
      screenResolution: 'unknown',
      colorDepth: 24,
      platform: 'server',
      cookieEnabled: false,
      doNotTrack: '0',
      plugins: [],
    };
  }

  const fingerprint: BrowserFingerprint = {
    userAgent: navigator.userAgent,
    acceptLanguage: navigator.language || 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    platform: navigator.platform || 'unknown',
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || '0',
    plugins: getPluginsList(),
  };

  // Optional: Canvas fingerprint
  try {
    fingerprint.canvas = getCanvasFingerprint();
  } catch {
    // Canvas fingerprint not available
  }

  // Optional: WebGL fingerprint
  try {
    fingerprint.webgl = getWebGLFingerprint();
  } catch {
    // WebGL fingerprint not available
  }

  return fingerprint;
}

/**
 * Get list of browser plugins
 */
function getPluginsList(): string[] {
  if (typeof navigator === 'undefined' || !navigator.plugins) {
    return [];
  }

  const plugins: string[] = [];
  for (let i = 0; i < navigator.plugins.length; i++) {
    plugins.push(navigator.plugins[i].name);
  }
  return plugins.slice(0, 10); // Limit to 10 plugins
}

/**
 * Generate canvas fingerprint
 */
function getCanvasFingerprint(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Draw text with specific font
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillStyle = '#f60';
  ctx.fillRect(125, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('Volovolo Pro', 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('Volovolo Pro', 4, 17);

  return canvas.toDataURL().slice(-50); // Last 50 chars as fingerprint
}

/**
 * Generate WebGL fingerprint
 */
function getWebGLFingerprint(): string {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
  if (!gl) return '';

  const webgl = gl as WebGLRenderingContext;
  const debugInfo = webgl.getExtension('WEBGL_debug_renderer_info');
  if (!debugInfo) return '';

  const vendor = webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
  const renderer = webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

  return `${vendor}~${renderer}`;
}

/**
 * Convert fingerprint to headers object for fetch requests
 * Note: User-Agent and DNT are browser-protected headers, so we use X- prefixed versions
 */
export function fingerprintToHeaders(fingerprint: BrowserFingerprint): Record<string, string> {
  return {
    'X-Fingerprint-User-Agent': fingerprint.userAgent,
    'X-Fingerprint-Accept-Language': fingerprint.acceptLanguage,
    'X-Timezone': fingerprint.timezone,
    'X-Screen-Resolution': fingerprint.screenResolution,
    'X-Color-Depth': String(fingerprint.colorDepth),
    'X-Platform': fingerprint.platform,
    'X-Cookie-Enabled': String(fingerprint.cookieEnabled),
    'X-Fingerprint-DNT': fingerprint.doNotTrack,
    'X-Plugins': fingerprint.plugins.join(','),
    ...(fingerprint.canvas && { 'X-Canvas-Fingerprint': fingerprint.canvas }),
    ...(fingerprint.webgl && { 'X-WebGL-Fingerprint': fingerprint.webgl }),
  };
}
